import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, MapPin, Users, Clock, Play, Square, 
  ArrowLeft, Plus, ChevronRight, BarChart3, Lock,
  Music, Trophy, Heart, X as XIcon, CheckCircle, Trash2,
  Copy, ExternalLink, QrCode, Zap, FileText, Download, Loader2,
  Edit, Eye, ListMusic, Share2, Globe, Search, Filter, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatDateTime, formatDate, formatDuration } from '@/utils/date';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// EVENT_TYPES - CONSTANTE GLOBALE - NE PAS MODIFIER LES VALUES (stockées en base)
// Les labels sont traduits dynamiquement via getEventTypes() dans le composant
const EVENT_TYPES = [
  { value: 'mariage', label: 'Mariage', emoji: '💒' },
  { value: 'club', label: 'Club / Discothèque', emoji: '🎉' },
  { value: 'bar', label: 'Bar / Pub', emoji: '🍻' },
  { value: 'anniversaire', label: 'Anniversaire', emoji: '🎂' },
  { value: 'entreprise', label: 'Événement d\'entreprise', emoji: '💼' },
  { value: 'autre', label: 'Autre', emoji: '🎵' },
];

export default function EventsPage() {
  const navigate = useNavigate();
  const { getAuthHeader, user, isPro } = useAuth();
  const { language, t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [copyError, setCopyError] = useState(null);

  // Event types with translations
  const getEventTypes = () => [
    { value: 'mariage', label: t('events', 'types')?.mariage || 'Mariage', emoji: '💒' },
    { value: 'club', label: t('events', 'types')?.club || 'Club', emoji: '🎉' },
    { value: 'bar', label: t('events', 'types')?.bar || 'Bar', emoji: '🍻' },
    { value: 'anniversaire', label: t('events', 'types')?.anniversaire || 'Anniversaire', emoji: '🎂' },
    { value: 'entreprise', label: t('events', 'types')?.entreprise || 'Entreprise', emoji: '💼' },
    { value: 'autre', label: t('events', 'types')?.autre || 'Autre', emoji: '🎵' },
  ];

  const getEventTypeLabel = (type) => {
    const types = getEventTypes();
    const found = types.find(t => t.value === type);
    return found ? `${found.emoji} ${found.label}` : type;
  };

  // Wrapper functions to use centralized date utils with current language
  const localFormatDate = (dateStr) => formatDate(dateStr, language);
  const localFormatDateTime = (dateStr) => formatDateTime(dateStr, language);

  // Status labels with translations
  const getEventDisplayStatus = (event) => {
    const today = new Date().toISOString().split('T')[0];
    const eventDate = event.date || event.event_date;
    
    const statusLabels = {
      fr: { closed: 'Clôturée', active: 'En cours', late: 'En retard', draft: 'Pré-événement', upcoming: 'À venir' },
      en: { closed: 'Closed', active: 'Active', late: 'Overdue', draft: 'Pre-event', upcoming: 'Upcoming' },
      es: { closed: 'Cerrado', active: 'Activo', late: 'Atrasado', draft: 'Pre-evento', upcoming: 'Próximo' },
      de: { closed: 'Beendet', active: 'Aktiv', late: 'Überfällig', draft: 'Vor-Event', upcoming: 'Kommend' },
      pt: { closed: 'Encerrado', active: 'Ativo', late: 'Atrasado', draft: 'Pré-evento', upcoming: 'Próximo' }
    };
    const labels = statusLabels[language] || statusLabels.fr;
    
    if (event.status === 'closed') {
      return { label: labels.closed, color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
    }
    if (event.status === 'active') {
      return { label: labels.active, color: 'bg-green-500/20 text-green-400 border-green-500/30' };
    }
    if (event.status === 'draft') {
      if (eventDate && eventDate < today) {
        return { label: labels.late, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
      }
      return { label: labels.draft, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
    }
    return { label: labels.upcoming, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
  };
  
  // Report states
  const [reportLoading, setReportLoading] = useState(null);
  const [customReportStart, setCustomReportStart] = useState('');
  const [customReportEnd, setCustomReportEnd] = useState('');
  
  // Filter state
  const [eventFilter, setEventFilter] = useState('upcoming'); // 'upcoming' or 'closed'
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [eventToActivate, setEventToActivate] = useState(null);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [selectedEventStats, setSelectedEventStats] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);
  
  // Event details modal state (for closed events)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Event requests modal state (new feature)
  const [requestsModalOpen, setRequestsModalOpen] = useState(false);
  const [eventRequests, setEventRequests] = useState([]);
  const [eventRequestsLoading, setEventRequestsLoading] = useState(false);
  const [selectedEventForRequests, setSelectedEventForRequests] = useState(null);
  const [requestsFilter, setRequestsFilter] = useState('all'); // all, pending, played, refused
  const [requestsSearch, setRequestsSearch] = useState('');
  const [requestsSort, setRequestsSort] = useState('created_at'); // created_at, likes
  
  // Edit event modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    event_type: '',
    location: '',
    approx_people: '',
    authority_label: '',
    authority_loves: '',
    authority_banned: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    event_type: 'club',
    date: new Date().toISOString().split('T')[0],
    location: '',
    approx_people: '',
    authority_label: '',
    authority_loves: '',
    authority_banned: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Copy link to clipboard with fallback
  const handleCopyLink = async (url, id, e) => {
    if (e) e.stopPropagation();
    if (!url) {
      setCopyError(id);
      setTimeout(() => setCopyError(null), 2000);
      return;
    }
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedUrl(id);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyError(id);
      setTimeout(() => setCopyError(null), 2000);
    }
  };

  // Open client form URL
  const handleOpenLink = (url, e) => {
    if (e) e.stopPropagation();
    if (!url) return;
    
    try {
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      // Fallback if popup blocked
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        window.location.href = url;
      }
    } catch (err) {
      window.location.href = url;
    }
  };

  // Share link (Web Share API or fallback)
  const handleShareLink = async (event, e) => {
    if (e) e.stopPropagation();
    const url = event.qr_url;
    if (!url) return;
    
    const shareData = {
      title: `TrackVibes - ${event.name}`,
      text: `Demande ta musique en live : ${event.name}`,
      url: url
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error - fallback to copy
        if (err.name !== 'AbortError') {
          handleCopyLink(url, event.id, e);
        }
      }
    } else {
      // Fallback: just copy the link
      handleCopyLink(url, event.id, e);
    }
  };

  // Download poster PDF with authentication
  const downloadPoster = async (eventId, format, e) => {
    if (e) e.stopPropagation();
    try {
      const response = await axios.get(
        `${API}/events/${eventId}/poster/${format}`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trackvibes-${format}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading poster:', error);
      // Handle blob error response
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          alert(json.detail || 'Erreur lors du téléchargement');
        } catch {
          alert('Erreur lors du téléchargement');
        }
      } else {
        alert(error.response?.data?.detail || 'Erreur lors du téléchargement');
      }
    }
  };

  const fetchEvents = useCallback(async () => {
    try {
      const [eventsRes, activeRes, globalStatsRes] = await Promise.all([
        axios.get(`${API}/events`, { headers: getAuthHeader() }),
        axios.get(`${API}/events/active`, { headers: getAuthHeader() }),
        axios.get(`${API}/stats/global`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      
      setEvents(eventsRes.data);
      setActiveEvent(activeRes.data.active_event);
      setGlobalStats(globalStatsRes.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleCreateEvent = async (startImmediately = false) => {
    setFormError('');
    
    if (!formData.name.trim()) {
      setFormError('Le nom de la soirée est requis');
      return;
    }
    
    // Validation: if loves or banned is filled, authority_label is required
    const hasLoves = formData.authority_loves.trim();
    const hasBanned = formData.authority_banned.trim();
    if ((hasLoves || hasBanned) && !formData.authority_label.trim()) {
      setFormError('Veuillez indiquer le donneur d\'ordre (ex : Mariés, Orga…)');
      return;
    }
    
    setFormLoading(true);
    try {
      // Parse authority_loves and authority_banned from comma-separated strings to arrays
      const authorityLoves = formData.authority_loves
        ? formData.authority_loves.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      const authorityBanned = formData.authority_banned
        ? formData.authority_banned.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      
      await axios.post(
        `${API}/events`,
        {
          ...formData,
          approx_people: formData.approx_people ? parseInt(formData.approx_people) : null,
          start_immediately: startImmediately,
          authority_label: formData.authority_label.trim() || null,
          authority_loves: authorityLoves.length > 0 ? authorityLoves : null,
          authority_banned: authorityBanned.length > 0 ? authorityBanned : null
        },
        { headers: getAuthHeader() }
      );
      
      setCreateModalOpen(false);
      setFormData({
        name: '',
        event_type: 'club',
        date: new Date().toISOString().split('T')[0],
        location: '',
        approx_people: '',
        authority_label: '',
        authority_loves: '',
        authority_banned: ''
      });
      await fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      setFormError(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const openActivateModal = (event) => {
    setEventToActivate(event);
    setActivateModalOpen(true);
  };

  const handleActivateEvent = async () => {
    if (!eventToActivate) return;
    
    setFormLoading(true);
    try {
      const response = await axios.post(
        `${API}/events/${eventToActivate.id}/activate`,
        {},
        { headers: getAuthHeader() }
      );
      
      setActivateModalOpen(false);
      setEventToActivate(null);
      alert(response.data.message || 'Soirée activée !');
      await fetchEvents();
    } catch (error) {
      console.error('Error activating event:', error);
      alert(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseEvent = async () => {
    if (!activeEvent) return;
    
    setFormLoading(true);
    try {
      const response = await axios.post(
        `${API}/events/${activeEvent.id}/close`,
        {},
        { headers: getAuthHeader() }
      );
      
      setCloseModalOpen(false);
      setSelectedEventStats({
        event: activeEvent,
        stats: response.data.stats
      });
      setStatsModalOpen(true);
      await fetchEvents();
    } catch (error) {
      console.error('Error closing event:', error);
      alert(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewStats = async (event) => {
    try {
      const response = await axios.get(
        `${API}/events/${event.id}/stats`,
        { headers: getAuthHeader() }
      );
      setSelectedEventStats(response.data);
      setStatsModalOpen(true);
    } catch (error) {
      console.error('Error fetching stats:', error);
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  // Fetch all requests for a specific event
  const handleViewEventRequests = async (event) => {
    setSelectedEventForRequests(event);
    setRequestsModalOpen(true);
    setEventRequestsLoading(true);
    setRequestsFilter('all');
    setRequestsSearch('');
    setRequestsSort('created_at');
    
    try {
      const response = await axios.get(
        `${API}/events/${event.id}/requests?status_filter=all&sort_by=created_at&sort_order=desc`,
        { headers: getAuthHeader() }
      );
      setEventRequests(response.data);
    } catch (error) {
      console.error('Error fetching event requests:', error);
      setEventRequests([]);
    } finally {
      setEventRequestsLoading(false);
    }
  };

  // Fetch filtered/sorted requests
  const fetchFilteredRequests = async (filter, search, sort) => {
    if (!selectedEventForRequests) return;
    
    setEventRequestsLoading(true);
    try {
      const params = new URLSearchParams({
        status_filter: filter,
        sort_by: sort,
        sort_order: sort === 'likes' ? 'desc' : 'desc'
      });
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await axios.get(
        `${API}/events/${selectedEventForRequests.id}/requests?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setEventRequests(response.data);
    } catch (error) {
      console.error('Error fetching filtered requests:', error);
    } finally {
      setEventRequestsLoading(false);
    }
  };

  // Handle filter/search/sort changes
  const handleRequestsFilterChange = (filter) => {
    setRequestsFilter(filter);
    fetchFilteredRequests(filter, requestsSearch, requestsSort);
  };

  const handleRequestsSearchChange = (search) => {
    setRequestsSearch(search);
    // Debounce search
    clearTimeout(window.requestsSearchTimeout);
    window.requestsSearchTimeout = setTimeout(() => {
      fetchFilteredRequests(requestsFilter, search, requestsSort);
    }, 300);
  };

  const handleRequestsSortChange = (sort) => {
    setRequestsSort(sort);
    fetchFilteredRequests(requestsFilter, requestsSearch, sort);
  };

  // View event details (for closed events)
  const handleViewDetails = async (event) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(
        `${API}/events/${event.id}/details`,
        { headers: getAuthHeader() }
      );
      setEventDetails(response.data);
      setDetailsModalOpen(true);
    } catch (error) {
      console.error('Error fetching event details:', error);
      alert(error.response?.data?.detail || 'Erreur');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (event) => {
    setEventToEdit(event);
    setEditFormData({
      name: event.name || '',
      event_type: event.event_type || 'autre',
      location: event.location || '',
      approx_people: event.approx_people?.toString() || '',
      authority_label: event.authority_label || '',
      authority_loves: (event.authority_loves || []).join(', '),
      authority_banned: (event.authority_banned || []).join(', ')
    });
    setEditModalOpen(true);
  };

  // Handle edit form submission
  const handleEditEvent = async () => {
    if (!eventToEdit) return;
    
    // Validation: if loves or banned is filled, authority_label is required
    const hasLoves = editFormData.authority_loves.trim();
    const hasBanned = editFormData.authority_banned.trim();
    if ((hasLoves || hasBanned) && !editFormData.authority_label.trim()) {
      setFormError('Veuillez indiquer le donneur d\'ordre (ex : Mariés, Orga…)');
      return;
    }
    
    setEditLoading(true);
    try {
      const updateData = {};
      if (editFormData.name) updateData.name = editFormData.name;
      if (editFormData.event_type) updateData.event_type = editFormData.event_type;
      if (editFormData.location !== undefined) updateData.location = editFormData.location;
      if (editFormData.approx_people) updateData.approx_people = parseInt(editFormData.approx_people);
      
      // Authority / music preferences
      updateData.authority_label = editFormData.authority_label.trim() || null;
      updateData.authority_loves = editFormData.authority_loves
        ? editFormData.authority_loves.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      updateData.authority_banned = editFormData.authority_banned
        ? editFormData.authority_banned.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      
      await axios.put(
        `${API}/events/${eventToEdit.id}/metadata`,
        updateData,
        { headers: getAuthHeader() }
      );
      
      setEditModalOpen(false);
      setEventToEdit(null);
      setFormError('');
      await fetchEvents();
      
      // If details modal was open, refresh it
      if (eventDetails && eventDetails.event.id === eventToEdit.id) {
        handleViewDetails(eventToEdit);
      }
    } catch (error) {
      console.error('Error updating event:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;
    
    setFormLoading(true);
    try {
      await axios.delete(
        `${API}/events/${eventToDelete.id}`,
        { headers: getAuthHeader() }
      );
      
      setDeleteModalOpen(false);
      setEventToDelete(null);
      await fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const openDeleteModal = (event) => {
    setEventToDelete(event);
    setDeleteModalOpen(true);
  };

  const handleToggleEventOpen = async (event) => {
    try {
      const response = await axios.post(
        `${API}/events/${event.id}/toggle-requests`,
        {},
        { headers: getAuthHeader() }
      );
      
      // Update local state for immediate UI feedback
      setEvents(prev => prev.map(e => 
        e.id === event.id ? { ...e, is_open: response.data.is_open } : e
      ));
      
      // Also update activeEvent if it's the same
      if (activeEvent && activeEvent.id === event.id) {
        setActiveEvent(prev => ({ ...prev, is_open: response.data.is_open }));
      }
    } catch (error) {
      console.error('Error toggling event requests:', error);
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadReport = async (type) => {
    setReportLoading(type);
    try {
      const endpoint = type === 'monthly' 
        ? `${API}/reports/monthly`
        : `${API}/reports/annual`;
      
      const response = await axios.get(endpoint, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = type === 'monthly' ? 'rapport-mensuel.pdf' : 'rapport-annuel.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert(error.response?.data?.detail || 'Erreur lors du téléchargement du rapport');
    } finally {
      setReportLoading(null);
    }
  };

  const handleCustomReport = async () => {
    if (!customReportStart || !customReportEnd) {
      alert('Veuillez sélectionner les dates de début et de fin');
      return;
    }
    
    // Validation
    const start = new Date(customReportStart);
    const end = new Date(customReportEnd);
    
    if (start > end) {
      alert('La date de début doit être avant la date de fin');
      return;
    }
    
    const daysDiff = (end - start) / (1000 * 60 * 60 * 24);
    if (daysDiff > 366) {
      alert('La plage maximale est de 1 an');
      return;
    }
    
    setReportLoading('custom');
    try {
      const response = await axios.post(
        `${API}/reports/custom`,
        { start_date: customReportStart, end_date: customReportEnd },
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      
      // Get filename
      const contentDisposition = response.headers['content-disposition'];
      let filename = `trackvibes-rapport-${customReportStart}_au_${customReportEnd}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading custom report:', error);
      alert(error.response?.data?.detail || 'Erreur lors du téléchargement du rapport');
    } finally {
      setReportLoading(null);
    }
  };

  // Paywall for FREE users - use centralized isPro() function
  if (!loading && !isPro()) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        <nav className="bg-[#0a0a0a] border-b border-white/10 px-6 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t('common', 'back')}
              </Button>
            </Link>
            <h1 className="font-heading text-xl text-white">{t('events', 'title')}</h1>
            <div className="w-24" />
          </div>
        </nav>
        
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8">
              <Lock className="w-16 h-16 text-neon-pink mx-auto mb-6" />
              <h2 className="font-heading text-2xl text-white mb-4">{t('events', 'pro_feature')}</h2>
              <p className="text-gray-400 mb-6">
                {t('events', 'pro_feature_desc')}
              </p>
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                <p className="text-neon-pink font-semibold mb-3">{t('events', 'pro_includes')}</p>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-neon-pink" />
                    {t('events', 'pro_create_events')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-neon-pink" />
                    {t('events', 'pro_detailed_stats')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-neon-pink" />
                    {t('events', 'pro_history')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-neon-pink" />
                    {t('events', 'pro_top_songs')}
                  </li>
                </ul>
              </div>
              <Button
                disabled
                className="w-full bg-neon-pink/50 text-white cursor-not-allowed"
              >
                {t('events', 'coming_soon')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-neon-cyan animate-pulse font-heading text-2xl">{t('common', 'loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="bg-[#0a0a0a] border-b border-white/10 px-6 py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t('common', 'back')}
            </Button>
          </Link>
          <h1 className="font-heading text-xl text-white">{t('events', 'title')}</h1>
          <Button
            onClick={() => setCreateModalOpen(true)}
            disabled={!!activeEvent}
            className="bg-neon-cyan text-black hover:bg-neon-cyan/80"
            data-testid="create-event-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('events', 'new_event')}
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto p-6">
        {/* Global Stats Summary */}
        {globalStats && globalStats.total_events > 0 && (
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-5 mb-6">
            <h3 className="font-heading text-sm text-gray-400 mb-4">{t('events', 'global_stats')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-heading text-neon-cyan">{globalStats.total_events}</p>
                <p className="text-gray-500 text-xs">{t('events', 'events_count')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-heading text-white">{globalStats.total_requests}</p>
                <p className="text-gray-500 text-xs">{t('events', 'total_requests')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-heading text-green-500">{globalStats.played_requests}</p>
                <p className="text-gray-500 text-xs">{t('events', 'played')}</p>
              </div>
              {globalStats.total_donations > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-heading text-neon-pink">{globalStats.total_donations.toFixed(0)}€</p>
                  <p className="text-gray-500 text-xs">{t('events', 'donations')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PRO Reports Section */}
        <div className="bg-gradient-to-br from-neon-pink/10 to-neon-cyan/10 border border-neon-pink/30 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-neon-pink" />
              {t('events', 'pro_reports')}
            </h3>
            <Badge className="bg-neon-pink text-white">👑 PRO</Badge>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            {t('events', 'download_reports')}
          </p>
          
          {/* Quick Reports */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <Button
              onClick={() => handleDownloadReport('monthly')}
              disabled={reportLoading === 'monthly'}
              variant="outline"
              className="border-neon-pink/30 text-white hover:bg-neon-pink/10 justify-start"
              data-testid="download-monthly-report-btn"
            >
              {reportLoading === 'monthly' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Calendar className="w-4 h-4 mr-2 text-neon-pink" />
              )}
              {t('events', 'monthly_report')}
            </Button>
            <Button
              onClick={() => handleDownloadReport('annual')}
              disabled={reportLoading === 'annual'}
              variant="outline"
              className="border-neon-pink/30 text-white hover:bg-neon-pink/10 justify-start"
              data-testid="download-annual-report-btn"
            >
              {reportLoading === 'annual' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trophy className="w-4 h-4 mr-2 text-neon-pink" />
              )}
              {t('events', 'annual_report')}
            </Button>
          </div>
          
          {/* Custom Report */}
          <div className="border-t border-white/10 pt-4">
            <p className="text-gray-300 text-sm font-medium mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('events', 'custom_report')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-gray-400 text-xs mb-1 block">{t('events', 'start_date')}</Label>
                <Input
                  type="date"
                  value={customReportStart}
                  onChange={(e) => setCustomReportStart(e.target.value)}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid="custom-report-start"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs mb-1 block">{t('events', 'end_date')}</Label>
                <Input
                  type="date"
                  value={customReportEnd}
                  onChange={(e) => setCustomReportEnd(e.target.value)}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid="custom-report-end"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleCustomReport}
                  disabled={reportLoading === 'custom' || !customReportStart || !customReportEnd}
                  className="w-full bg-neon-pink hover:bg-neon-pink/80 text-white"
                  data-testid="download-custom-report-btn"
                >
                  {reportLoading === 'custom' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {t('events', 'generate')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Active Event Banner */}
        {activeEvent && (
          <div className="bg-gradient-to-r from-neon-cyan/20 to-neon-pink/20 border border-neon-cyan/30 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-neon-cyan/20 rounded-full flex items-center justify-center">
                  <Play className="w-6 h-6 text-neon-cyan" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">{t('events', 'event_in_progress')}</p>
                  <h2 className="font-heading text-xl text-white">{activeEvent.name}</h2>
                  <p className="text-gray-400 text-sm">
                    {getEventTypeLabel(activeEvent.event_type)}
                    {activeEvent.location && ` • ${activeEvent.location}`}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleViewStats(activeEvent)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="view-live-stats-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {t('events', 'live_stats')}
                </Button>
                <Button
                  onClick={() => setCloseModalOpen(true)}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="close-event-btn"
                >
                  <Square className="w-4 h-4 mr-2" />
                  {t('events', 'close')}
                </Button>
              </div>
            </div>
            {activeEvent.started_at && (
              <div className="mt-4 flex items-center gap-2 text-gray-400 text-sm">
                <Clock className="w-4 h-4" />
                {t('common', 'started_at')} {localFormatDateTime(activeEvent.started_at)}
              </div>
            )}
          </div>
        )}

        {/* Events List with Tabs */}
        <div>
          {/* Filter Tabs */}
          <div className="flex items-center gap-4 mb-4">
            <h3 className="font-heading text-lg text-white">{t('events', 'my_events')}</h3>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => setEventFilter('upcoming')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  eventFilter === 'upcoming'
                    ? 'bg-neon-cyan text-black'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
                data-testid="filter-upcoming"
              >
                {t('events', 'upcoming')} ({events.filter(e => e.status !== 'closed').length})
              </button>
              <button
                onClick={() => setEventFilter('closed')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  eventFilter === 'closed'
                    ? 'bg-gray-500 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
                data-testid="filter-closed"
              >
                {t('events', 'closed')} ({events.filter(e => e.status === 'closed').length})
              </button>
            </div>
          </div>
          
          {/* Filtered Events */}
          {(() => {
            const filteredEvents = events
              .filter(e => eventFilter === 'closed' ? e.status === 'closed' : e.status !== 'closed')
              .sort((a, b) => {
                const dateA = a.date || a.event_date || '0000-00-00';
                const dateB = b.date || b.event_date || '0000-00-00';
                // "À venir" : tri croissant (du plus proche au plus éloigné)
                // "Clôturées" : tri décroissant (du plus récent au plus ancien)
                if (eventFilter === 'upcoming') {
                  return dateA.localeCompare(dateB); // Ascending: soonest first
                }
                return dateB.localeCompare(dateA); // Descending: most recent first
              });
            
            if (filteredEvents.length === 0) {
              return (
                <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-8 text-center">
                  <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">
                    {eventFilter === 'closed' 
                      ? t('events', 'no_closed')
                      : t('events', 'no_upcoming')}
                  </p>
                  {eventFilter === 'upcoming' && (
                    <p className="text-gray-500 text-sm mt-2">
                      {t('events', 'create_first_hint')}
                    </p>
                  )}
                </div>
              );
            }
            
            return (
              <div className="space-y-4">
                {filteredEvents.map((event) => {
                  const displayStatus = getEventDisplayStatus(event);
                  return (
                    <div
                      key={event.id}
                      className={`bg-[#0a0a0a] border rounded-xl p-4 sm:p-5 transition-all ${
                        event.status === 'active' 
                          ? 'border-neon-cyan/50 bg-neon-cyan/5' 
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`event-card-${event.id}`}
                    >
                      <div className="flex flex-col gap-3 sm:gap-4">
                        {/* Event Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-white text-sm sm:text-base">{event.name}</h4>
                            <Badge className={displayStatus.color + ' border text-xs'}>
                              {displayStatus.label}
                            </Badge>
                            {event.requests_count > 0 && (
                              <span className="text-neon-pink text-xs sm:text-sm">
                                {event.requests_count} {event.requests_count > 1 ? t('events', 'requests_plural') : t('events', 'requests')}
                              </span>
                            )}
                          </div>
                          {/* Toggle is_open for draft/active events */}
                          {(event.status === 'draft' || event.status === 'active') && (
                            <div className="flex items-center gap-2 self-start sm:self-auto">
                              <span className={`text-xs ${event.is_open !== false ? 'text-green-400' : 'text-gray-500'}`}>
                                {event.is_open !== false ? t('events', 'open') : t('events', 'closed_status')}
                              </span>
                              <Switch
                                checked={event.is_open !== false}
                                onCheckedChange={() => handleToggleEventOpen(event)}
                                className="data-[state=checked]:bg-green-500"
                                data-testid={`event-toggle-${event.id}`}
                              />
                            </div>
                          )}
                        </div>

                        {/* Event Info */}
                        <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Music className="w-4 h-4" />
                            {getEventTypeLabel(event.event_type)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {localFormatDate(event.date)}
                          </span>
                          {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                      )}
                      {event.approx_people && (
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          ~{event.approx_people} pers.
                        </span>
                      )}
                    </div>

                    {/* Event Actions - QR URL for draft/active events */}
                    {(event.status === 'draft' || event.status === 'active') && (
                      <div className="bg-white/5 rounded-lg p-3">
                        {event.qr_url ? (
                          <div className="space-y-3">
                            {/* Primary action - Open form */}
                            <Button
                              onClick={(e) => handleOpenLink(event.qr_url, e)}
                              className="w-full bg-gradient-to-r from-neon-pink to-purple-500 text-white hover:opacity-90"
                              size="sm"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              {t('events', 'open_form')}
                            </Button>
                            
                            {/* Secondary actions */}
                            <div className="flex gap-2">
                              <Button
                                onClick={(e) => handleCopyLink(event.qr_url, event.id, e)}
                                size="sm"
                                variant="outline"
                                className={`flex-1 border-white/20 ${
                                  copiedUrl === event.id 
                                    ? 'text-green-400 border-green-500/50' 
                                    : copyError === event.id
                                    ? 'text-red-400 border-red-500/50'
                                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                                }`}
                              >
                                {copiedUrl === event.id ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    {t('events', 'link_copied')}
                                  </>
                                ) : copyError === event.id ? (
                                  <>
                                    <XIcon className="w-4 h-4 mr-2" />
                                    {t('events', 'copy_failed')}
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-4 h-4 mr-2" />
                                    {t('events', 'copy')}
                                  </>
                                )}
                              </Button>
                              
                              <Button
                                onClick={(e) => handleShareLink(event, e)}
                                size="sm"
                                variant="outline"
                                className="flex-1 border-white/20 text-gray-300 hover:text-white hover:bg-white/10"
                              >
                                <Share2 className="w-4 h-4 mr-2" />
                                {t('events', 'share')}
                              </Button>
                            </div>
                            
                            {/* Poster download buttons */}
                            <div className="flex gap-2 pt-2 border-t border-white/10">
                              <Button
                                onClick={(e) => downloadPoster(event.id, 'A4', e)}
                                size="sm"
                                variant="outline"
                                className="flex-1 border-white/20 text-gray-300 hover:text-white hover:bg-white/10"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                {t('events', 'poster_a4')}
                              </Button>
                              <Button
                                onClick={(e) => downloadPoster(event.id, 'A5', e)}
                                size="sm"
                                variant="outline"
                                className="flex-1 border-white/20 text-gray-300 hover:text-white hover:bg-white/10"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                {t('events', 'poster_a5')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-2">
                            <span className="text-gray-500 text-sm">{t('events', 'link_unavailable')}</span>
                          </div>
                        )}
                      </div>
                    )}
                      
                    {/* Quick stats for closed events */}
                    {event.status === 'closed' && event.stats_snapshot && (
                      <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                        <span className="text-neon-cyan">
                          {event.stats_snapshot.total_requests} {t('events', 'requests_plural')}
                        </span>
                        <span className="text-green-500">
                          {event.stats_snapshot.played_count} {t('events', 'played')}
                        </span>
                        {event.stats_snapshot.duration_minutes && (
                          <span className="text-gray-400">
                            {formatDuration(event.stats_snapshot.duration_minutes)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
                      {event.status === 'draft' && (
                        <>
                          <Button
                            onClick={() => openActivateModal(event)}
                            size="sm"
                            className="bg-neon-cyan text-black hover:bg-neon-cyan/80"
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">{t('events', 'activate')}</span>
                            <span className="sm:hidden">Go</span>
                          </Button>
                          <Button
                            onClick={() => openEditModal(event)}
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-gray-300 hover:bg-white/10"
                            data-testid={`edit-draft-event-${event.id}`}
                          >
                            <Edit className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">{t('events', 'modify')}</span>
                          </Button>
                          <Button
                            onClick={() => openDeleteModal(event)}
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {event.status === 'closed' && (
                        <>
                          <Button
                            onClick={() => handleViewDetails(event)}
                            size="sm"
                            className="bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan/30 border border-neon-cyan/30"
                            data-testid={`view-details-${event.id}`}
                          >
                            <Eye className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">{t('events', 'view_details')}</span>
                          </Button>
                          <Button
                            onClick={() => handleViewEventRequests(event)}
                            size="sm"
                            variant="outline"
                            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                            data-testid={`view-requests-${event.id}`}
                          >
                            <ListMusic className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">{t('events', 'view_requests')}</span>
                          </Button>
                          <Button
                            onClick={() => openEditModal(event)}
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-gray-300 hover:bg-white/10"
                            data-testid={`edit-event-${event.id}`}
                          >
                            <Edit className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">{t('events', 'modify')}</span>
                          </Button>
                          <Button
                            onClick={() => openDeleteModal(event)}
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Create Event Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{t('modals', 'create_title')}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('modals', 'create_subtitle')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {formError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {formError}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_name')} *</Label>
              <Input
                placeholder={t('modals', 'event_name_placeholder')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="event-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_type')}</Label>
              <Select
                value={formData.event_type}
                onValueChange={(value) => setFormData({ ...formData, event_type: value })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {getEventTypes().map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white">
                      {type.emoji} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_date')}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_location')} ({t('common', 'optional')})</Label>
              <Input
                placeholder={t('modals', 'event_location_placeholder')}
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_guests')}</Label>
              <Input
                type="number"
                placeholder="Ex: 150"
                value={formData.approx_people}
                onChange={(e) => setFormData({ ...formData, approx_people: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            
            {/* Authority / Music Preferences Section */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <Label className="text-neon-pink flex items-center gap-2 mb-3">
                <Music className="w-4 h-4" />
                {t('modals', 'authority_title')} ({t('common', 'optional')})
              </Label>
              
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm text-gray-400">{t('modals', 'authority_label')}</Label>
                  <Input
                    placeholder={t('modals', 'authority_label_placeholder')}
                    value={formData.authority_label}
                    onChange={(e) => setFormData({ ...formData, authority_label: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="authority-label-input"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm text-gray-400">🚫 {t('modals', 'authority_banned')}</Label>
                  <Input
                    placeholder={t('modals', 'authority_banned_placeholder')}
                    value={formData.authority_banned}
                    onChange={(e) => setFormData({ ...formData, authority_banned: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="authority-banned-input"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm text-gray-400">❤️ {t('modals', 'authority_loves')}</Label>
                  <Input
                    placeholder={t('modals', 'authority_loves_placeholder')}
                    value={formData.authority_loves}
                    onChange={(e) => setFormData({ ...formData, authority_loves: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="authority-loves-input"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleCreateEvent(false)}
              disabled={formLoading}
              className="flex-1 border-white/20 text-white hover:bg-white/10"
            >
              {t('modals', 'create_confirm')}
            </Button>
            <Button
              onClick={() => handleCreateEvent(true)}
              disabled={formLoading}
              className="flex-1 bg-neon-cyan text-black hover:bg-neon-cyan/80"
              data-testid="create-and-start-btn"
            >
              {formLoading ? t('common', 'loading') : `${t('modals', 'create_confirm')} & ${t('modals', 'activate_confirm')}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Event Confirmation Modal */}
      <Dialog open={closeModalOpen} onOpenChange={setCloseModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{t('modals', 'close_title')}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('modals', 'close_warning')}
            </DialogDescription>
          </DialogHeader>
          
          {activeEvent && (
            <div className="py-4">
              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="font-semibold text-white mb-2">{activeEvent.name}</h4>
                <p className="text-gray-400 text-sm">{getEventTypeLabel(activeEvent.event_type)}</p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCloseModalOpen(false)}
              className="flex-1 border-white/20 text-white hover:bg-white/10"
            >
              {t('common', 'cancel')}
            </Button>
            <Button
              onClick={handleCloseEvent}
              disabled={formLoading}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-close-btn"
            >
              {formLoading ? t('common', 'loading') : t('modals', 'close_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={statsModalOpen} onOpenChange={setStatsModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-neon-cyan" />
              {t('modals', 'stats_title')}
              {selectedEventStats?.is_live && (
                <Badge className="bg-green-500 text-white text-xs">LIVE</Badge>
              )}
            </DialogTitle>
            {selectedEventStats?.event && (
              <DialogDescription className="text-gray-400">
                {selectedEventStats.event.name} • {getEventTypeLabel(selectedEventStats.event.event_type)}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {selectedEventStats?.stats && (
            <div className="py-4 space-y-6">
              {/* Main Stats Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-3xl font-heading text-neon-cyan">
                    {selectedEventStats.stats.total_requests}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">{t('events', 'requests_plural')}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-3xl font-heading text-green-500">
                    {selectedEventStats.stats.played_count}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">{t('events', 'played')}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-3xl font-heading text-neon-pink">
                    {selectedEventStats.stats.unique_requesters}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">{t('events', 'participants')}</p>
                </div>
              </div>
              
              {/* Secondary Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{selectedEventStats.stats.pending_count}</p>
                    <p className="text-gray-400 text-xs">{t('events', 'pending')}</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <XIcon className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{selectedEventStats.stats.rejected_count}</p>
                    <p className="text-gray-400 text-xs">{t('events', 'refused')}</p>
                  </div>
                </div>
              </div>
              
              {/* Tips & Duration */}
              <div className="flex gap-3">
                {selectedEventStats.stats.total_tips > 0 && (
                  <div className="flex-1 bg-gradient-to-r from-neon-pink/20 to-purple-500/20 border border-neon-pink/30 rounded-xl p-4">
                    <p className="text-neon-pink text-sm">{t('events', 'total_tips')}</p>
                    <p className="text-2xl font-heading text-white">
                      {selectedEventStats.stats.total_tips.toFixed(2)} €
                    </p>
                  </div>
                )}
                {selectedEventStats.stats.duration_minutes && (
                  <div className="flex-1 bg-white/5 rounded-xl p-4">
                    <p className="text-gray-400 text-sm">{t('events', 'duration')}</p>
                    <p className="text-2xl font-heading text-white">
                      {formatDuration(selectedEventStats.stats.duration_minutes)}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Top Songs */}
              {selectedEventStats.stats.top_songs?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <Trophy className="w-4 h-4 text-yellow-500" />
                    {t('events', 'top_songs')}
                  </h4>
                  <div className="space-y-2">
                    {selectedEventStats.stats.top_songs.slice(0, 5).map((song, index) => (
                      <div key={index} className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                        <span className="text-xl font-heading text-gray-500 w-6">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white truncate">{song.song_title}</p>
                          {song.artist && (
                            <p className="text-gray-400 text-sm truncate">{song.artist}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Heart className="w-4 h-4 text-neon-pink" />
                          <span className="text-neon-pink">{song.likes}</span>
                        </div>
                        <Badge 
                          className={
                            song.status === 'played' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-gray-500/20 text-gray-400'
                          }
                        >
                          {song.status === 'played' ? t('events', 'played_status') : song.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setStatsModalOpen(false)}
              className="w-full bg-neon-cyan text-black hover:bg-neon-cyan/80"
            >
              {t('common', 'close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Requests Modal */}
      <Dialog open={requestsModalOpen} onOpenChange={setRequestsModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ListMusic className="w-5 h-5 text-purple-400" />
              {t('events', 'event_requests')}
            </DialogTitle>
            {selectedEventForRequests && (
              <DialogDescription className="text-gray-400">
                {selectedEventForRequests.name} • {eventRequests.length} {t('events', 'requests_plural')}
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-3 py-3 flex-shrink-0 border-b border-white/10">
            {/* Status Filter */}
            <div className="flex gap-1 flex-wrap">
              {[
                { value: 'all', label: t('events', 'all_requests') },
                { value: 'pending', label: t('events', 'pending_requests') },
                { value: 'played', label: t('events', 'played_requests') },
                { value: 'refused', label: t('events', 'refused_requests') }
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleRequestsFilterChange(filter.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    requestsFilter === filter.value
                      ? 'bg-purple-500 text-white'
                      : 'bg-white/10 text-gray-400 hover:bg-white/20'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={requestsSearch}
                onChange={(e) => handleRequestsSearchChange(e.target.value)}
                placeholder={t('events', 'search_requests')}
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-sm text-white placeholder:text-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>

            {/* Sort */}
            <select
              value={requestsSort}
              onChange={(e) => handleRequestsSortChange(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-purple-500 focus:outline-none"
            >
              <option value="created_at">{t('events', 'sort_by_date')}</option>
              <option value="likes">{t('events', 'sort_by_likes')}</option>
            </select>
          </div>

          {/* Requests List */}
          <div className="flex-1 overflow-y-auto py-3 space-y-2 min-h-0">
            {eventRequestsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : eventRequests.length === 0 ? (
              <div className="text-center py-12">
                <Music className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">{t('events', 'no_requests')}</p>
                <p className="text-gray-500 text-sm">{t('events', 'no_requests_hint')}</p>
              </div>
            ) : (
              eventRequests.map((request, index) => (
                <div
                  key={request.id}
                  className={`bg-white/5 rounded-xl p-3 sm:p-4 border transition-all ${
                    request.status === 'played'
                      ? 'border-green-500/30'
                      : request.status === 'refused'
                      ? 'border-red-500/30'
                      : 'border-white/10'
                  }`}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    {/* Index */}
                    <span className="text-gray-500 text-xs sm:text-sm font-mono w-5 sm:w-6 flex-shrink-0">
                      {index + 1}.
                    </span>

                    {/* Song Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                        <h4 className="text-white font-medium text-sm sm:text-base truncate max-w-[150px] sm:max-w-none">{request.song_title}</h4>
                        {request.bpm && (
                          <Badge className="bg-purple-500/20 text-purple-400 text-[10px] sm:text-xs font-mono flex-shrink-0">
                            {request.bpm}
                          </Badge>
                        )}
                      </div>
                      {request.artist && (
                        <p className="text-gray-400 text-xs sm:text-sm truncate">{request.artist}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 text-[10px] sm:text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span className="truncate max-w-[80px] sm:max-w-none">{request.client_name}</span>
                        </span>
                        {request.created_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(request.created_at).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Likes & Status */}
                    <div className="flex flex-col items-end gap-1 sm:gap-2 flex-shrink-0">
                      <Badge
                        className={`text-[10px] sm:text-xs ${
                          request.status === 'played'
                            ? 'bg-green-500/20 text-green-400'
                            : request.status === 'refused'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}
                      >
                        {request.status === 'played'
                          ? t('events', 'played_status')
                          : request.status === 'refused'
                          ? t('events', 'refused')
                          : t('events', 'pending')}
                      </Badge>
                      {request.likes > 0 && (
                        <span className="flex items-center gap-1 text-neon-pink">
                          <Heart className="w-3 h-3 fill-current" />
                          {request.likes}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="flex-shrink-0 pt-3 border-t border-white/10">
            <Button
              onClick={() => setRequestsModalOpen(false)}
              className="w-full bg-purple-500 text-white hover:bg-purple-600"
            >
              {t('common', 'close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Event Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-400">{t('modals', 'delete_title')}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('modals', 'delete_warning')}
            </DialogDescription>
          </DialogHeader>
          
          {eventToDelete && (
            <div className="py-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <h4 className="font-semibold text-white mb-2">{eventToDelete.name}</h4>
                <p className="text-gray-400 text-sm">{getEventTypeLabel(eventToDelete.event_type)}</p>
                {eventToDelete.stats_snapshot && (
                  <p className="text-red-400 text-sm mt-2">
                    {eventToDelete.stats_snapshot.total_requests} {t('dashboard', 'requests')}
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setEventToDelete(null);
              }}
              className="flex-1 border-white/20 text-white hover:bg-white/10"
            >
              {t('common', 'cancel')}
            </Button>
            <Button
              onClick={handleDeleteEvent}
              disabled={formLoading}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              {formLoading ? t('common', 'loading') : t('modals', 'delete_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Event Modal */}
      <Dialog open={activateModalOpen} onOpenChange={setActivateModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-neon-cyan">{t('modals', 'activate_title')}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('modals', 'activate_warning')}
            </DialogDescription>
          </DialogHeader>
          
          {eventToActivate && (
            <div className="py-4">
              <div className="bg-neon-cyan/10 border border-neon-cyan/30 rounded-xl p-4">
                <h4 className="font-semibold text-white mb-2">{eventToActivate.name}</h4>
                <p className="text-gray-400 text-sm">{getEventTypeLabel(eventToActivate.event_type)}</p>
                {eventToActivate.requests_count > 0 && (
                  <p className="text-neon-cyan text-sm mt-2">
                    <Zap className="w-4 h-4 inline mr-1" />
                    {eventToActivate.requests_count} {t('dashboard', 'requests')}
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setActivateModalOpen(false);
                setEventToActivate(null);
              }}
              className="flex-1 border-white/20 text-white hover:bg-white/10"
            >
              {t('common', 'cancel')}
            </Button>
            <Button
              onClick={handleActivateEvent}
              disabled={formLoading}
              className="flex-1 bg-neon-cyan text-black hover:bg-neon-cyan/80"
              data-testid="confirm-activate-btn"
            >
              {formLoading ? t('common', 'loading') : t('modals', 'activate_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Details Modal */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ListMusic className="w-5 h-5 text-neon-cyan" />
              {t('modals', 'stats_title')}
            </DialogTitle>
            {eventDetails?.event && (
              <DialogDescription className="text-gray-400">
                {eventDetails.event.name} • {getEventTypeLabel(eventDetails.event.event_type)}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 text-neon-cyan animate-spin mx-auto mb-2" />
              <p className="text-gray-400">{t('common', 'loading')}</p>
            </div>
          ) : eventDetails ? (
            <div className="py-4 space-y-5">
              <div className="bg-white/5 rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">{t('modals', 'event_date')}</p>
                  <p className="text-white">{localFormatDate(eventDetails.event.date)}</p>
                </div>
                {eventDetails.event.location && (
                  <div>
                    <p className="text-gray-400">{t('modals', 'event_location')}</p>
                    <p className="text-white">{eventDetails.event.location}</p>
                  </div>
                )}
                {eventDetails.event.approx_people && (
                  <div>
                    <p className="text-gray-400">{t('modals', 'event_guests')}</p>
                    <p className="text-white">~{eventDetails.event.approx_people}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-400">{t('dashboard', 'requests')}</p>
                  <p className="text-white">{eventDetails.requests?.length || 0}</p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={() => { setDetailsModalOpen(false); openEditModal(eventDetails.event); }} variant="outline" className="border-white/20 text-gray-300 hover:bg-white/10" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  {t('common', 'edit')}
                </Button>
              </div>

              {eventDetails.top_styles?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <Music className="w-4 h-4 text-neon-pink" />
                    {t('modals', 'stats_top_styles')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {eventDetails.top_styles.slice(0, 6).map((style) => (
                      <div key={style.style} className="bg-neon-pink/10 border border-neon-pink/20 rounded-lg px-3 py-2">
                        <span className="text-white text-sm">{style.style}</span>
                        <span className="text-neon-pink text-xs ml-2">({style.count})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {eventDetails.stats && (
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-xl font-heading text-neon-cyan">{eventDetails.stats.total_requests}</p>
                    <p className="text-gray-400 text-xs">Total</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-xl font-heading text-green-500">{eventDetails.stats.played_count}</p>
                    <p className="text-gray-400 text-xs">{t('dashboard', 'played')}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-xl font-heading text-red-500">{eventDetails.stats.rejected_count}</p>
                    <p className="text-gray-400 text-xs">{t('dashboard', 'refused_label')}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-xl font-heading text-neon-pink">{eventDetails.stats.unique_requesters}</p>
                    <p className="text-gray-400 text-xs">{t('modals', 'event_guests')}</p>
                  </div>
                </div>
              )}

              {eventDetails.requests?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <ListMusic className="w-4 h-4 text-neon-cyan" />
                    {t('dashboard', 'requests')} ({eventDetails.requests.length})
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {eventDetails.requests.map((req) => (
                      <div key={req.id} className="bg-white/5 rounded-lg p-2 flex items-center justify-between text-sm">
                        <div className="min-w-0 flex-1">
                          <p className="text-white truncate">{req.song_title}</p>
                          <p className="text-gray-500 text-xs truncate">{req.client_name}</p>
                        </div>
                        <Badge className={req.status === 'played' ? 'bg-green-500/20 text-green-400' : req.status === 'refused' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}>
                          {req.status === 'played' ? t('dashboard', 'played') : req.status === 'refused' ? t('dashboard', 'refused_label') : req.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button onClick={() => setDetailsModalOpen(false)} className="w-full bg-neon-cyan text-black hover:bg-neon-cyan/80">
              {t('common', 'close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Event Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-neon-cyan" />
              {t('modals', 'edit_title')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {formError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {formError}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_name')}</Label>
              <Input value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_type')}</Label>
              <Select value={editFormData.event_type} onValueChange={(value) => setEditFormData({ ...editFormData, event_type: value })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {getEventTypes().map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white">
                      {type.emoji} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_location')}</Label>
              <Input value={editFormData.location} onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })} placeholder={t('modals', 'event_location_placeholder')} className="bg-white/5 border-white/10 text-white" />
            </div>
            
            <div className="space-y-2">
              <Label>{t('modals', 'event_guests')}</Label>
              <Input type="number" value={editFormData.approx_people} onChange={(e) => setEditFormData({ ...editFormData, approx_people: e.target.value })} placeholder="Ex: 150" className="bg-white/5 border-white/10 text-white" />
            </div>
            
            {/* Authority / Music Preferences Section */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <Label className="text-neon-pink flex items-center gap-2 mb-3">
                <Music className="w-4 h-4" />
                {t('modals', 'authority_title')}
              </Label>
              
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm text-gray-400">{t('modals', 'authority_label')}</Label>
                  <Input
                    placeholder={t('modals', 'authority_label_placeholder')}
                    value={editFormData.authority_label}
                    onChange={(e) => setEditFormData({ ...editFormData, authority_label: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-authority-label-input"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm text-gray-400">🚫 {t('modals', 'authority_banned')}</Label>
                  <Input
                    placeholder={t('modals', 'authority_banned_placeholder')}
                    value={editFormData.authority_banned}
                    onChange={(e) => setEditFormData({ ...editFormData, authority_banned: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-authority-banned-input"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm text-gray-400">❤️ {t('modals', 'authority_loves')}</Label>
                  <Input
                    placeholder={t('modals', 'authority_loves_placeholder')}
                    value={editFormData.authority_loves}
                    onChange={(e) => setEditFormData({ ...editFormData, authority_loves: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-authority-loves-input"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditModalOpen(false); setEventToEdit(null); setFormError(''); }} className="flex-1 border-white/20 text-white hover:bg-white/10">
              {t('common', 'cancel')}
            </Button>
            <Button onClick={handleEditEvent} disabled={editLoading} className="flex-1 bg-neon-cyan text-black hover:bg-neon-cyan/80">
              {editLoading ? t('common', 'loading') : t('modals', 'edit_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
